import type {ReactElement} from 'react';
import React from 'react';
import {BaseItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-shadow-option';
import type {ItemShadowProperties} from '@PosterWhiteboard/classes/item-aura.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromAuraOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromAuraOfActiveItem';
import type {RGBA} from '@Utils/color.util';

interface ItemShadowOptionProps {
  onClickColorFill(): void;
}

export function ItemShadowOption({onClickColorFill = (): void => {}}: ItemShadowOptionProps): ReactElement {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const auraType = useGetKeyFromAuraOfActiveItem('type');
  const glowColor = useGetKeyFromAuraOfActiveItem('glowColor');
  const activeItemDropShadowAngle = useGetKeyFromAuraOfActiveItem('dropShadowAngle');
  const activeItemDropShadowColor = useGetKeyFromAuraOfActiveItem('dropShadowColor');
  const activeItemDropShadowDistance = useGetKeyFromAuraOfActiveItem('dropShadowDistance');
  const activeItemDropShadowBlur = useGetKeyFromAuraOfActiveItem('dropShadowBlur');

  if (
    !activeItem ||
    (activeItem && !('aura' in activeItem)) ||
    activeItemDropShadowDistance === undefined ||
    activeItemDropShadowBlur === undefined ||
    !activeItemDropShadowColor ||
    activeItemDropShadowAngle === undefined ||
    auraType === undefined ||
    !glowColor
  ) {
    throw new Error('item does not have aura property');
  }

  const updateShadowObject = (newProperties: ItemShadowProperties): void => {
    void activeItem.updateFromObject({
      aura: newProperties,
    });
  };

  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    const dropShadowColor = activeItemDropShadowColor.slice() as RGBA;
    dropShadowColor[3] = dropShadowAlpha;
    void activeItem.updateFromObject(
      {
        aura: {
          dropShadowColor,
        },
      },
      {
        undoable,
      }
    );
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        aura: {
          dropShadowAngle,
        },
      },
      {
        undoable,
      }
    );
  };

  const updateDistance = (dropShadowDistance: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        aura: {
          dropShadowDistance,
        },
      },
      {
        undoable,
      }
    );
  };

  const updateBlur = (dropShadowBlur: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        aura: {
          dropShadowBlur,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseItemShadowOption
      onClickColorFill={(): void => {
        onClickColorFill();
      }}
      onToggleShadow={updateShadowObject}
      auraObject={{
        type: auraType,
        glowColor: glowColor,
        dropShadowAngle: activeItemDropShadowAngle,
        dropShadowBlur: activeItemDropShadowBlur,
        dropShadowDistance: activeItemDropShadowDistance,
        dropShadowColor: activeItemDropShadowColor,
      }}
      itemType={activeItem.gitype}
      onUpdateOpacity={updateOpacity}
      onUpdateAngle={updateAngle}
      onUpdateDistance={updateDistance}
      onUpdateBlur={updateBlur}
    />
  );
}
